import { FC, useCallback, useMemo, useState } from 'react';
// mui components
import { Grid, IconButton, Typography } from '@mui/material';
// components
import DialogModal from 'components/modal/dialog-modal';
import UserCard from '../user-card';
// api
import { IProjectProfiles, IProjectsProfiles } from 'redux-store/api/projects';
import { useSetContactPersonMutation } from 'redux-store/api/projects/projects.api';
// icons
import CrossIcon from 'assets/icons/buttons/cross.svg';
// styles
import useToast from 'hooks/useToast';
import { useUserInfo } from 'hooks/useUserInfo';
import { styles } from './styles';

interface IUsersModalProps {
  type: 'single user' | 'multiple users';
  profiles: IProjectProfiles[];
  projectProfiles: IProjectsProfiles[];
  open: boolean;
  closeModal: () => void;
  projectId: number;
}

const UsersModal: FC<IUsersModalProps> = ({
  type,
  closeModal,
  open,
  profiles,
  projectProfiles,
  projectId,
}) => {
  const showToast = useToast();
  const { isOwner, currentRole } = useUserInfo();

  const [setContactPerson, { isLoading: isSettingContact }] = useSetContactPersonMutation();
  const [selectedContactPersonId, setSelectedContactPersonId] = useState<number | null>(null);

  const canAssignContactPerson = Boolean(isOwner || currentRole?.name === 'ADMIN');

  const handleContactPerson = useCallback(
    (profile: IProjectProfiles) => {
      if (!canAssignContactPerson) {
        return;
      }

      setSelectedContactPersonId(profile.id);
      setContactPerson({
        projectId,
        profileId: profile.id,
        isContactPerson: true,
      })
        .catch((error) => {
          showToast({ type: 'error', message: error?.message || 'An unexpected error occurred.' });
        })
        .finally(() => {
          setSelectedContactPersonId(null);
        });
    },
    [canAssignContactPerson, projectId, setContactPerson, showToast]
  );

  // Owner/Admin can assign contact person to any role, including themselves
  const updatedProfiles = useMemo(
    () =>
      profiles.map((profile) => ({
        ...profile,
        isAllowedSelectContact: canAssignContactPerson,
      })),
    [canAssignContactPerson, profiles]
  );

  return (
    <DialogModal
      open={open}
      handleClose={closeModal}
      maxWidth="700px"
      withoutPadding
      contentSx={{
        overflowY: 'clip',
        maxWidth: 'unset',
      }}
    >
      <Grid sx={styles.container}>
        <Grid
          sx={{
            ...styles.header,
            padding: type === 'multiple users' ? '40px 40px 20px' : 'unset',
            height: type === 'multiple users' ? 'unset' : '52px',
          }}
        >
          {type === 'multiple users' ? (
            <>
              <Typography sx={styles.title}>Project Users</Typography>
              <Typography sx={styles.subTitle}>
                Below are the users included in this project.
              </Typography>
            </>
          ) : null}
          <IconButton sx={styles.crossBtn}>
            <CrossIcon onClick={closeModal} />
          </IconButton>
        </Grid>
        <Grid sx={styles.contentWrapper}>
          <Grid sx={styles.contentContainer}>
            {updatedProfiles.map((profile) => (
              <UserCard
                profile={profile}
                projectProfiles={projectProfiles.find((pp) => pp.profileId === profile.id)}
                key={profile.id}
                customStyles={{
                  width: type === 'multiple users' ? 'calc( (100% - 12px) / 2 )' : '100%',
                }}
                closeModal={closeModal}
                onContactPersonSelected={handleContactPerson}
                loading={isSettingContact}
                selectedContactPersonId={selectedContactPersonId}
              />
            ))}
          </Grid>
        </Grid>
      </Grid>
    </DialogModal>
  );
};

export default UsersModal;
